import {
    Box,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    VStack,
    Text,
    Table,
    Thead,
    Tbody,
    Tfoot,
    Tr,
    Th,
    Td,
    Flex,
    HStack,
    Icon,
    IconButton,
    Tooltip,
    Checkbox,
    Collapse,
    Tag,
    useToast,
} from '@chakra-ui/react';
import { Fragment, useContext, useMemo, useState } from 'react';
import {
    TbArrowBackUp,
    TbCheck,
    TbChevronDown,
    TbChevronUp,
    TbCircleCheck,
    TbBuildingWarehouse,
    TbInfoCircle,
    TbPackage,
    TbTruck,
    TbX,
} from 'react-icons/tb';
import {
    OrderedTab,
    OrderedPart,
    SupplierOrderStatus,
    OrderedSection as OrderedSectionInterface,
    SupplierNote,
} from '../../hooks/useGetOrderDetail';
import { formatPrice, formatPercentage } from '../../functions';
import PartType from '../PartType';
import PartNumber from '../PartNumber';
import SupplierPartNumber from './SupplierPartNumber';
import SourceBadge from '../SourceBadge';
import PurchaseOrderPdfButton from '../inventory/PurchaseOrderPdfButton';
import SortArrowIcon from '../SortArrowIcon';
import VendorSelector from '../VendorSelector';
import CollapsibleSearch from '../CollapsibleSearch';
import SupplierOrderInfoPopover from '../SupplierOrderInfoPopover';
import BulkActionBar, { BulkActionBarItem } from '../BulkActionBar';
import CancelReturnPartsModal, { CancelReturnPartSummary } from '../../modals/CancelReturnPartsModal';
import MoveToInventoryConfirmModal, {
    MoveToInventoryPartSnapshot,
} from '../../modals/MoveToInventoryConfirmModal';
import { useAcceptSupplierChanges } from '../../hooks/useAcceptSupplierChanges';
import { useReceiveParts } from '../../hooks/useReceiveParts';
import { useConfirmSupplierOrder } from '../../hooks/useConfirmSupplierOrder';
import { Button, ConfirmDialog, Notice } from '../../ui';
import { getSupplierChangeSummary } from '../../utils/supplierChangeNotice';
import SessionContext from '../../contexts/SessionContext';
import { canMarkPartsReceived } from '../../utils/deliveryReceiveAccess';
import {
    canMovePartsToInventory,
    getOrderedPartRemainingQuantity,
} from '../../utils/canMovePartsToInventory';

const isUnconfirmedInPartstrader = (section: OrderedSectionInterface) => section.source === 'partstrader'
    && ((section.status === 'ordered' && !section.hasChanges) || !!section.awaitingPartstraderConfirmation);

const getStatusColor = (status: SupplierOrderStatus) => {
    switch (status) {
        case 'confirmed':
            return 'purple';
        case 'ordered':
            return 'teal';
        case 'rejected':
            return 'gray';
        case 'cancelled':
            return 'red';
    }
};

const getStatusLabel = (status: SupplierOrderStatus) => {
    switch (status) {
        case 'confirmed':
            return 'Confirmed';
        case 'ordered':
            return 'Ordered';
        case 'rejected':
            return 'Rejected';
        case 'cancelled':
            return 'Cancelled';
    }
};

/** True when the part should render a "Received by" attribution bar. */
const hasReceivedNote = (part: OrderedPart): boolean => !!part.received && !!part.receivedByName;

const isPartReceivable = (part: OrderedPart, sectionStatus: SupplierOrderStatus): boolean => (
    (sectionStatus === 'ordered' || sectionStatus === 'confirmed')
    && !part.cancelled
    && !part.returned
    && !part.received
);

const isPartUnreceivable = (part: OrderedPart): boolean => !!part.received;

const isPartReturnable = (part: OrderedPart, sectionStatus: SupplierOrderStatus): boolean => (
    // canReturn already encodes the per-source status rules (partstrader/ccc
    // allow cancel/return while still 'ordered'; opstrax requires 'confirmed')
    // — don't re-restrict to confirmed here, just exclude dead sections.
    !!part.canReturn
    && (sectionStatus === 'ordered' || sectionStatus === 'confirmed')
    && !part.cancelled
    && !part.returned
);

const isPartInventoryMovable = (part: OrderedPart, sectionStatus: SupplierOrderStatus): boolean => (
    sectionStatus === 'confirmed'
    && !part.cancelled
    && !part.returned
    && getOrderedPartRemainingQuantity(part) > 0
);

const isPartSelectable = (
    part: OrderedPart,
    sectionStatus: SupplierOrderStatus,
    orgCanReceive: boolean,
    canSelectForInventory: boolean,
    canConfirmSection: boolean,
): boolean => (
    isPartReturnable(part, sectionStatus)
    || (orgCanReceive && (isPartReceivable(part, sectionStatus) || isPartUnreceivable(part)))
    || (canSelectForInventory && isPartInventoryMovable(part, sectionStatus))
    || (canConfirmSection && !part.cancelled && !part.returned)
);

const receiveCheckboxSx = {
    '&[data-checked]': {
        bg: '#0071E3',
        borderColor: '#0071E3',
    },
    '&[data-checked]:hover': {
        bg: '#005BC4',
        borderColor: '#005BC4',
    },
} as const;

/**
 * Full-width attribution bar rendered as a companion row directly under a
 * received part (styled after the note bar in PartOptionsModal). The part row
 * above suppresses its own bottom border (see receivedRowSx) so this bar
 * visually belongs to that part, with the row divider falling below the bar.
 */
const ReceivedByRow = ({
    part,
    colSpan,
    canUndo = false,
    isUndoPending = false,
    onUndo,
}: {
    part: OrderedPart;
    colSpan: number;
    canUndo?: boolean;
    isUndoPending?: boolean;
    onUndo?: (part: OrderedPart) => void;
}) => {
    if (!hasReceivedNote(part)) return null;

    const receivedAtLabel = part.receivedAt
        ? new Date(part.receivedAt).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        })
        : null;

    return (
        <Tr>
            {/* Spacer under the checkbox column so the bar's left edge lines up
                with the Part Name column, not the checkbox. */}
            <Td w="40px" px={4} pt={1} pb={4} />
            <Td colSpan={colSpan - 1} pt={1} pb={4} pl={4} pr={4}>
                <HStack
                    spacing={2}
                    align="center"
                    border="1px solid"
                    borderColor="green.200"
                    bg="green.50"
                    borderRadius="md"
                    px={3}
                    py={2}
                    w="100%"
                >
                    <Icon
                        as={TbCircleCheck as React.ElementType}
                        w={4}
                        h={4}
                        color="green.500"
                        flexShrink={0}
                    />
                    <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
                        Received by: <Text as="span" fontWeight="semibold" color="gray.700">{part.receivedByName}</Text>
                    </Text>
                    {receivedAtLabel && (
                        <Text fontSize="sm" color="gray.400" whiteSpace="nowrap">
                            • {receivedAtLabel}
                        </Text>
                    )}
                    {canUndo && onUndo && (
                        <Tooltip label="Undo received" placement="top" hasArrow>
                            <IconButton
                                aria-label="Undo received"
                                icon={<Icon as={TbArrowBackUp as React.ElementType} boxSize={4} />}
                                size="xs"
                                variant="ghost"
                                color="gray.500"
                                ml="auto"
                                _hover={{ color: 'gray.700', bg: 'green.100' }}
                                isDisabled={isUndoPending}
                                onClick={() => onUndo(part)}
                            />
                        </Tooltip>
                    )}
                </HStack>
            </Td>
        </Tr>
    );
};

/** Drops the part row's bottom border so its ReceivedByRow hugs it. */
const receivedRowSx = { td: { borderBottom: 'none' } } as const;

interface OrderedTabsProps {
    tabs: Array<OrderedTab>;
    refetch: () => void | Promise<unknown>;
    vin: string;
    supplierNotes?: Array<SupplierNote>;
    supplierMessageCounts?: Record<string, number>;
    onOpenSupplierCommunications?: (supplierOrderId: string, initialTab: 'quoting-comments' | 'order-messages') => void;
    locationId?: string;
    roNumber?: string;
    vehicleMake?: string;
    quotesOnly?: boolean;
}

type SortField = 'listPrice' | 'buyPrice' | 'grossMargin';
type SortDirection = 'asc' | 'desc';

interface SortState {
    field: SortField | null;
    direction: SortDirection;
}

const sortPartsByField = (parts: OrderedPart[], sortState: SortState) => {
    if (!sortState.field) {
        return parts;
    }

    const sortableParts = [...parts];

    const getValue = (part: OrderedPart): number | null => {
        switch (sortState.field) {
            case 'listPrice': return part.listPrice ?? null;
            case 'buyPrice': return part.buyPrice ?? null;
            case 'grossMargin': return part.grossMargin ?? null;
            default: return null;
        }
    };

    sortableParts.sort((a, b) => {
        const aValue = getValue(a);
        const bValue = getValue(b);

        if (aValue === null || aValue === undefined) {
            return bValue === null || bValue === undefined ? 0 : (sortState.direction === 'asc' ? 1 : -1);
        }

        if (bValue === null || bValue === undefined) {
            return sortState.direction === 'asc' ? -1 : 1;
        }

        if (aValue === bValue) {
            return 0;
        }

        return sortState.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return sortableParts;
};

interface SortableHeaderProps {
    label: string;
    field: SortField;
    sortState: SortState;
    onSort: (field: SortField) => void;
}

const SortableHeader = ({ label, field, sortState, onSort }: SortableHeaderProps) => {
    const isActive = sortState.field === field;
    const direction = isActive ? sortState.direction : 'none';

    return (
        <Flex
            as="button"
            type="button"
            align="center"
            justify="flex-end"
            gap="6px"
            w="100%"
            color={isActive ? "#005BC4" : "gray.600"}
            fontSize="xs"
            fontWeight="semibold"
            textTransform="uppercase"
            letterSpacing="0.05em"
            cursor="pointer"
            onClick={() => onSort(field)}
        >
            <Text as="span">
                {label}
            </Text>
            <Box lineHeight={0} color={isActive ? "#005BC4" : "gray.400"}>
                <SortArrowIcon direction={direction} />
            </Box>
        </Flex>
    );
};

interface OrderedSectionProps {
    section: OrderedSectionInterface;
    showStatusTag?: boolean;
    sortState: SortState;
    onToggleSort: (field: SortField) => void;
    selectedParts: Set<string>;
    onTogglePartSelection: (partId: string) => void;
    onToggleAllParts: (sectionKey: string, partIds: string[]) => void;
    refetch: () => void | Promise<unknown>;
    vin: string;
    searchQuery?: string;
    supplierNotes?: Array<SupplierNote>;
    supplierMessageCounts?: Record<string, number>;
    onOpenSupplierCommunications?: (supplierOrderId: string, initialTab: 'quoting-comments' | 'order-messages') => void;
    orgCanReceive?: boolean;
    canSelectForInventory?: boolean;
    canMarkConfirmed?: boolean;
    isReceivePending?: boolean;
    onUndoReceive?: (part: OrderedPart) => void;
}

const OrderedSection = ({
    section,
    showStatusTag = false,
    sortState,
    onToggleSort,
    selectedParts,
    onTogglePartSelection,
    onToggleAllParts,
    refetch,
    vin,
    searchQuery = '',
    supplierNotes = [],
    supplierMessageCounts = {},
    onOpenSupplierCommunications,
    orgCanReceive = false,
    canSelectForInventory = false,
    canMarkConfirmed = false,
    isReceivePending = false,
    onUndoReceive,
}: OrderedSectionProps) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const toast = useToast();
    const acceptSupplierChanges = useAcceptSupplierChanges();

    const supplierCommunicationCount = useMemo(() => {
        const notesForSupplier = supplierNotes.filter((note) => note.supplierName === section.title).length;
        const messagesForSupplier = supplierMessageCounts[section.key] ?? 0;
        return notesForSupplier + messagesForSupplier;
    }, [supplierNotes, supplierMessageCounts, section.title, section.key]);
    const supplierCommunicationInitialTab = (
        section.source === 'opstrax' || (supplierMessageCounts[section.key] ?? 0) > 0
    )
        ? 'order-messages'
        : 'quoting-comments';

    const searchFilteredParts = useMemo(() => {
        if (!searchQuery.trim()) return section.parts;
        const q = searchQuery.toLowerCase().trim();
        return section.parts.filter((part) =>
            part.name.toLowerCase().includes(q) ||
            (part.partNumber && part.partNumber.toLowerCase().includes(q)) ||
            (part.correctedPartNumber && part.correctedPartNumber.toLowerCase().includes(q)) ||
            (part.supplierPartNumber && part.supplierPartNumber.toLowerCase().includes(q)) ||
            (part.type && part.type.toLowerCase().includes(q)) ||
            (part.delivery && part.delivery.toLowerCase().includes(q)) ||
            (part.vendor && part.vendor.toLowerCase().includes(q)) ||
            section.title.toLowerCase().includes(q)
        );
    }, [section.parts, section.title, searchQuery]);

    const parts = sortPartsByField(searchFilteredParts, sortState);
    // Changed parts queued for the reject (cancel/return) flow.
    const [rejectPartIds, setRejectPartIds] = useState<string[] | null>(null);

    const changeSummary = useMemo(() => getSupplierChangeSummary({
        hasChanges: section.hasChanges,
        source: section.source,
        partstraderConfirmed: section.status === 'confirmed' && !section.awaitingPartstraderConfirmation,
        parts: section.parts
            .filter(p => !p.cancelled && !p.returned)
            .map(p => ({
                id: p.partId,
                buyPrice: p.buyPrice,
                correctedBuyPrice: p.correctedBuyPrice,
                listPrice: p.listPrice,
                correctedListPrice: p.correctedListPrice,
                partNumber: p.partNumber,
                correctedPartNumber: p.correctedPartNumber,
                eta: p.delivery,
                correctedEta: p.correctedDelivery,
            })),
    }), [section.parts, section.hasChanges, section.source, section.status, section.awaitingPartstraderConfirmation]);

    const changeNotice = changeSummary.notice;
    const showChangeActions = changeSummary.actionablePartIds.length > 0;

    const rejectWholeOrder = isUnconfirmedInPartstrader(section);
    const rejectScopePartIds = useMemo(() => (
        rejectWholeOrder
            ? section.parts.filter(p => !p.cancelled && !p.returned).map(p => p.partId)
            : changeSummary.actionablePartIds
    ), [rejectWholeOrder, section.parts, changeSummary.actionablePartIds]);

    const handleAccept = async () => {
        try {
            await acceptSupplierChanges.mutateAsync({
                supplierOrderId: section.key,
            });
            toast({
                title: 'Accepting supplier changes',
                description: 'The changes are being applied. Please check back shortly.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            refetch();
        } catch {
            toast({
                title: 'Failed to accept supplier changes',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const canConfirmSection = canMarkConfirmed && !!section.canManualConfirm;
    const selectableParts = parts.filter((p) =>
        isPartSelectable(p, section.status, orgCanReceive, canSelectForInventory, canConfirmSection),
    );
    const allSelectableSelected = selectableParts.length > 0
        && selectableParts.every((p) => selectedParts.has(p.partId));
    const shouldShowSupplierCommunicationsIcon = (
        ['ordered', 'confirmed'].includes(section.status) &&
        (section.source === 'partstrader' || section.source === 'opstrax' || section.source === 'ccc')
    );

    const content = (
        <>
            {/* Supplier Name and Contact Info */}
            <Flex px={6} pt={5} pb={4} justify="space-between" align="center">
                <HStack spacing={3} align="center">
                    {section.source && (
                        <SourceBadge source={section.source} />
                    )}
                    <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                        {section.title}
                    </Text>
                    {section.displayId && (
                        <Text
                            fontSize="sm"
                            color="gray.500"
                            fontWeight="medium"
                            bg="gray.50"
                            border="1px solid"
                            borderColor="gray.200"
                            borderRadius="md"
                            px={2}
                            py={0.5}
                            lineHeight="1.2"
                        >
                            {section.displayId}
                        </Text>
                    )}
                    {section.source === 'inventory' &&
                        section.purchaseOrderId &&
                        section.invoiceNumber && (
                            <PurchaseOrderPdfButton
                                purchaseOrderId={section.purchaseOrderId}
                                invoiceNumber={section.invoiceNumber}
                                variant="icon"
                            />
                        )}
                    {shouldShowSupplierCommunicationsIcon && (
                        <Tooltip label="Supplier Communications" placement="top">
                            <Box position="relative">
                                <IconButton
                                    aria-label={`Open supplier communications for ${section.title}`}
                                    icon={<Icon as={TbTruck as React.ElementType} boxSize="18px" strokeWidth={2.25} />}
                                    boxSize="24px"
                                    minW="24px"
                                    h="24px"
                                    variant="ghost"
                                    color="orange.500"
                                    onClick={() => onOpenSupplierCommunications?.(section.key, supplierCommunicationInitialTab)}
                                    isDisabled={!onOpenSupplierCommunications}
                                />
                                {supplierCommunicationCount > 0 && (
                                    <Flex
                                        position="absolute"
                                        top="-4px"
                                        right="-4px"
                                        minW="14px"
                                        h="14px"
                                        px="3px"
                                        borderRadius="full"
                                        bg="orange.500"
                                        color="white"
                                        align="center"
                                        justify="center"
                                        fontSize="9px"
                                        fontWeight="bold"
                                        lineHeight="1"
                                    >
                                        {supplierCommunicationCount}
                                    </Flex>
                                )}
                            </Box>
                        </Tooltip>
                    )}
                    <SupplierOrderInfoPopover
                        label="Contact Info"
                        phone={section.phoneNumber}
                        email={section.email}
                    />
                </HStack>
                <HStack spacing={3}>
                    {section.cccPoCreationStatus && (
                        <Tooltip
                            label={
                                section.cccPoCreationStatus === 'failed' && section.cccPoCreationFailedReason ? section.cccPoCreationFailedReason :
                                    `Manual PO creation ${section.cccPoCreationStatus === 'completed' ? 'succeeded' : 'failed'}`
                            }
                            placement="top"
                            hasArrow
                        >
                            <Flex
                                align="center"
                                justify="center"
                                boxSize="22px"
                                borderRadius="full"
                                bg={section.cccPoCreationStatus === 'completed' ? 'green.50' : 'red.50'}
                                color={section.cccPoCreationStatus === 'completed' ? 'green.600' : 'red.600'}
                                border="1px solid"
                                borderColor={section.cccPoCreationStatus === 'completed' ? 'green.200' : 'red.200'}
                                flexShrink={0}
                            >
                                <Icon
                                    as={(section.cccPoCreationStatus === 'completed' ? TbCheck : TbX) as React.ElementType}
                                    boxSize={3.5}
                                    strokeWidth={2.5}
                                />
                            </Flex>
                        </Tooltip>
                    )}
                    <HStack spacing={2}>
                        {/* A fully-received supplier order supersedes its status tag
                            (ordered -> confirmed -> received) and shows on every tab. */}
                        {(showStatusTag || section.received) && (
                            <>
                                <Tag
                                    size="sm"
                                    colorScheme={section.received ? 'green' : getStatusColor(section.status)}
                                    variant="subtle"
                                    borderRadius="md"
                                    fontWeight="medium"
                                    fontSize="xs"
                                >
                                    {section.received ? 'Received' : getStatusLabel(section.status)}
                                </Tag>
                                <Text fontSize="sm" color="gray.600" fontWeight="semibold">•</Text>
                            </>
                        )}
                        <Text fontSize="sm" color="gray.600" fontWeight='semibold'>
                            {new Date(section.timePlaced).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} • {new Date(section.timePlaced).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </Text>
                    </HStack>
                    <IconButton
                        aria-label={isCollapsed ? `Expand ${section.title}` : `Collapse ${section.title}`}
                        icon={<Icon as={(isCollapsed ? TbChevronDown : TbChevronUp) as React.ElementType} boxSize={4} />}
                        size="sm"
                        variant="ghost"
                        color="gray.600"
                        onClick={() => setIsCollapsed((prev) => !prev)}
                    />
                </HStack>
            </Flex>

            <Collapse
                in={!isCollapsed}
                animateOpacity
                transition={{ enter: { duration: 0.35 }, exit: { duration: 0.25 } }}
            >
                {changeNotice &&
                    <Box mb={4} px={6}>
                        <Notice
                            tone={changeNotice.tone}
                            title={changeNotice.title}
                            action={showChangeActions && (
                                <HStack spacing={2} pl={4} flexShrink={0}>
                                    <Button
                                        size="xs"
                                        variant="outline"
                                        onClick={() => setRejectPartIds(rejectScopePartIds)}
                                        isDisabled={rejectScopePartIds.length === 0 || acceptSupplierChanges.isPending}
                                    >
                                        Reject Change
                                    </Button>
                                    <Button
                                        size="xs"
                                        variant="solid"
                                        onClick={() => handleAccept()}
                                        isLoading={acceptSupplierChanges.isPending}
                                        loadingText="Accepting..."
                                    >
                                        Accept Change
                                    </Button>
                                </HStack>
                            )}
                        >
                            {changeNotice.body}
                        </Notice>
                    </Box>}

                {/* Parts Table */}
                <Box borderTop="1px solid" borderColor="gray.200" overflowX="auto">
                    <Table variant="simple" size="sm" w="100%" minW="930px" sx={{ tableLayout: 'fixed', 'th, td': { verticalAlign: 'middle' }, 'th:first-of-type, td:first-of-type': { pl: 6 }, 'th:last-of-type, td:last-of-type': { pr: 6 } }}>
                        <Thead>
                            <Tr bg="gray.50">
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="50px">
                                    <Checkbox
                                        isChecked={allSelectableSelected}
                                        onChange={() => onToggleAllParts(section.key, selectableParts.map((part) => part.partId))}
                                        isDisabled={selectableParts.length === 0}
                                        size="md"
                                        sx={receiveCheckboxSx}
                                    />
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="200px">
                                    PART NAME
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="120px">
                                    OE PART #
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="120px">
                                    SUPPLIER PART #
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="80px" textAlign="center">
                                    QTY
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="120px">
                                    DELIVERY ETA
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="110px" isNumeric>
                                    <SortableHeader label="LIST PRICE" field="listPrice" sortState={sortState} onSort={onToggleSort} />
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="110px" isNumeric>
                                    <SortableHeader label="BUY PRICE" field="buyPrice" sortState={sortState} onSort={onToggleSort} />
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} px={4} w="120px" isNumeric>
                                    <SortableHeader label="GROSS MARGIN" field="grossMargin" sortState={sortState} onSort={onToggleSort} />
                                </Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {parts.map((part, partIndex) => {
                                // A rejected or wholly-cancelled supplier order kills every part in
                                // it, even when the integration never flagged parts individually —
                                // render them red-struck like part-level cancellations. Returned
                                // parts keep their blue treatment.
                                const sectionKilled = section.status === 'rejected' || section.status === 'cancelled';
                                const showAsCancelled = part.cancelled || (sectionKilled && !part.returned);
                                const inactive = showAsCancelled || part.returned;
                                const rowColor = showAsCancelled ? 'red' : part.returned ? 'blue' : null;
                                const textColor = showAsCancelled ? 'red.500' : part.returned ? 'blue.500' : 'gray.900';
                                return (
                                    <Fragment key={part.partId}>
                                    <Tr
                                        _hover={{ bg: rowColor ? `${rowColor}.50` : "gray.25" }}
                                        bg={rowColor ? `${rowColor}.50` : undefined}
                                        opacity={inactive ? 0.6 : 1}
                                        sx={hasReceivedNote(part) ? receivedRowSx : undefined}
                                    >
                                        <Td py={4} px={4} w="40px">
                                            <Flex align="center" h="100%">
                                                <Checkbox
                                                    isChecked={selectedParts.has(part.partId)}
                                                    onChange={() => onTogglePartSelection(part.partId)}
                                                    isDisabled={!isPartSelectable(part, section.status, orgCanReceive, canSelectForInventory, canConfirmSection)}
                                                    size="md"
                                                    sx={receiveCheckboxSx}
                                                />
                                            </Flex>
                                        </Td>
                                        <Td py={4} px={4}>
                                            <Flex align="center" h="100%">
                                                <VStack align="flex-start" spacing={1}>
                                                    <HStack spacing={1.5}>
                                                        <Text fontSize="sm" fontWeight="medium" color={textColor} textDecoration={inactive ? 'line-through' : undefined}>
                                                            {part.name}
                                                        </Text>
                                                        {part.returned && (
                                                            <Tooltip label="Returned" hasArrow placement="top">
                                                                <Box display="inline-flex">
                                                                    <Icon as={TbArrowBackUp as React.ElementType} w={3.5} h={3.5} color="blue.400" />
                                                                </Box>
                                                            </Tooltip>
                                                        )}
                                                    </HStack>
                                                    {part.type ?
                                                        <PartType
                                                            partType={part.type}
                                                        /> : null}
                                                </VStack>
                                            </Flex>
                                        </Td>
                                        <Td py={4} px={4}>
                                            <Flex align="center" h="100%">
                                                <Box textDecoration={inactive ? 'line-through' : undefined} color={rowColor ? `${rowColor}.500` : undefined}>
                                                    <PartNumber
                                                        partNumber={part.partNumber}
                                                        correctedPartNumber={part.correctedPartNumber}
                                                        vin={vin}

                                                    />
                                                </Box>
                                            </Flex>
                                        </Td>
                                        <Td py={4} px={4}>
                                            <Flex align="center" h="100%">
                                                <SupplierPartNumber
                                                    partNumber={part.partNumber}
                                                    correctedPartNumber={part.correctedPartNumber}
                                                    supplierPartNumber={part.supplierPartNumber}
                                                    inactive={inactive}
                                                    textColor={textColor}
                                                />
                                            </Flex>
                                        </Td>
                                        <Td py={4} px={4}>
                                            <Flex align="center" justify="center" h="100%" gap={1}>
                                                {(() => {
                                                    const inventoried = Math.max(0, part.quantityInventoried ?? 0);
                                                    const remaining = getOrderedPartRemainingQuantity(part);
                                                    if (inventoried > 0) {
                                                        return (
                                                            <>
                                                                <VStack align="center" spacing={0}>
                                                                    <Text
                                                                        fontSize="sm"
                                                                        color="gray.500"
                                                                        textDecoration="line-through"
                                                                    >
                                                                        {part.quantity ?? '-'}
                                                                    </Text>
                                                                    <Text
                                                                        fontSize="sm"
                                                                        color={inactive ? textColor : 'orange.500'}
                                                                        fontWeight="medium"
                                                                        textDecoration={inactive ? 'line-through' : undefined}
                                                                    >
                                                                        {remaining}
                                                                    </Text>
                                                                </VStack>
                                                                <Tooltip
                                                                    label={`${inventoried} parts inventoried`}
                                                                    placement="top"
                                                                    hasArrow
                                                                >
                                                                    <Box as="span" display="inline-flex" flexShrink={0}>
                                                                        <Icon
                                                                            as={TbInfoCircle as React.ElementType}
                                                                            w={3.5}
                                                                            h={3.5}
                                                                            color="gray.400"
                                                                        />
                                                                    </Box>
                                                                </Tooltip>
                                                            </>
                                                        );
                                                    }
                                                    return (
                                                        <Text
                                                            fontSize="sm"
                                                            color={textColor}
                                                            textDecoration={inactive ? 'line-through' : undefined}
                                                        >
                                                            {part.quantity ?? '-'}
                                                        </Text>
                                                    );
                                                })()}
                                            </Flex>
                                        </Td>
                                        <Td py={4} px={4}>
                                            <Flex align="center" h="100%">
                                                {part.correctedDelivery && part.correctedDelivery !== part.delivery ? (
                                                    <VStack align="flex-start" spacing={0}>
                                                        <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                                                            {part.delivery || '-'}
                                                        </Text>
                                                        <Text fontSize="sm" color={inactive ? textColor : 'orange.500'} fontWeight="medium" textDecoration={inactive ? 'line-through' : undefined}>
                                                            {part.correctedDelivery}
                                                        </Text>
                                                    </VStack>
                                                ) : (
                                                    <Text fontSize="sm" color={textColor} textDecoration={inactive ? 'line-through' : undefined}>
                                                        {part.delivery || '-'}
                                                    </Text>
                                                )}
                                            </Flex>
                                        </Td>
                                        <Td py={4} px={4} isNumeric>
                                            <Flex align="center" justify="flex-end" h="100%">
                                                {part.correctedListPrice && part.correctedListPrice !== part.listPrice ? (
                                                    <VStack align="flex-end" spacing={0}>
                                                        <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                                                            {part.listPrice ? formatPrice(part.listPrice) : '-'}
                                                        </Text>
                                                        <Text fontSize="sm" fontWeight="medium" color={inactive ? textColor : 'orange.500'} textDecoration={inactive ? 'line-through' : undefined}>
                                                            {formatPrice(part.correctedListPrice)}
                                                        </Text>
                                                    </VStack>
                                                ) : (
                                                    <Text fontSize="sm" fontWeight="medium" color={textColor} textDecoration={inactive ? 'line-through' : undefined}>
                                                        {part.listPrice ? formatPrice(part.listPrice) : '-'}
                                                    </Text>
                                                )}
                                            </Flex>
                                        </Td>
                                        <Td py={4} px={4} isNumeric>
                                            <Flex align="center" justify="flex-end" h="100%">
                                                {part.correctedBuyPrice && part.correctedBuyPrice !== part.buyPrice ? (
                                                    <VStack align="flex-end" spacing={0}>
                                                        <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                                                            {part.buyPrice ? formatPrice(part.buyPrice) : '-'}
                                                        </Text>
                                                        <Text fontSize="sm" fontWeight="medium" color={inactive ? textColor : 'orange.500'} textDecoration={inactive ? 'line-through' : undefined}>
                                                            {formatPrice(part.correctedBuyPrice)}
                                                        </Text>
                                                    </VStack>
                                                ) : (
                                                    <Text fontSize="sm" fontWeight="medium" color={textColor} textDecoration={inactive ? 'line-through' : undefined}>
                                                        {part.buyPrice ? formatPrice(part.buyPrice) : '-'}
                                                    </Text>
                                                )}
                                            </Flex>
                                        </Td>
                                        <Td py={4} px={4} isNumeric>
                                            <Flex align="center" justify="flex-end" h="100%">
                                                {part.correctedGrossMargin && part.correctedGrossMargin !== part.grossMargin ? (
                                                    <VStack align="flex-end" spacing={0}>
                                                        <Text fontSize="sm" color="gray.500" textDecoration="line-through">
                                                            {formatPercentage(part.grossMargin)}
                                                        </Text>
                                                        <Text fontSize="sm" color={inactive ? textColor : 'orange.500'} textDecoration={inactive ? 'line-through' : undefined}>
                                                            {formatPercentage(part.correctedGrossMargin)}
                                                        </Text>
                                                    </VStack>
                                                ) : (
                                                    <Text fontSize="sm" color={textColor} textDecoration={inactive ? 'line-through' : undefined}>
                                                        {formatPercentage(part.grossMargin)}
                                                    </Text>
                                                )}
                                            </Flex>
                                        </Td>
                                    </Tr>
                                    <ReceivedByRow
                                        part={part}
                                        colSpan={9}
                                        canUndo={orgCanReceive}
                                        isUndoPending={isReceivePending}
                                        onUndo={onUndoReceive}
                                    />
                                    </Fragment>
                                );
                            })}
                        </Tbody>
                        <Tfoot>
                            <Tr bg="gray.50" borderTop="2px solid" borderTopColor="gray.200">
                                <Td colSpan={6} py={3} px={4}>
                                    <Text fontSize="sm" fontWeight="bold" color="gray.700">
                                        SUBTOTAL
                                    </Text>
                                </Td>
                                <Td py={3} px={4} isNumeric>
                                    <Text fontSize="sm" fontWeight="bold" color="gray.900">
                                        {formatPrice(section.totalListPrice)}
                                    </Text>
                                </Td>
                                <Td py={3} px={4} isNumeric>
                                    <Text fontSize="sm" fontWeight="bold" color="gray.900">
                                        {formatPrice(section.totalBuyPrice)}
                                    </Text>
                                </Td>
                                <Td py={3} px={4} isNumeric>
                                    <Text fontSize="sm" fontWeight="bold" color="gray.900">
                                        {formatPercentage(section.grossMargin)}
                                    </Text>
                                </Td>
                            </Tr>
                        </Tfoot>
                    </Table>
                </Box>
            </Collapse>

            <CancelReturnPartsModal
                isOpen={rejectPartIds !== null}
                onClose={() => setRejectPartIds(null)}
                supplierOrderId={section.key}
                source={section.source}
                cancelOnly={rejectWholeOrder}
                selectedPartIds={rejectPartIds ?? []}
                parts={(rejectPartIds ?? [])
                    .map((partId) => section.parts.find((p) => p.partId === partId))
                    .filter((p): p is NonNullable<typeof p> => !!p)
                    .map((p) => ({
                        partId: p.partId,
                        name: p.name,
                        partNumber: p.correctedPartNumber ?? p.partNumber,
                    }))}
                onSuccess={() => {
                    refetch();
                    setRejectPartIds(null);
                }}
            />
        </>
    );

    return content;
};

type SectionSortState = Record<string, SortState>;
const defaultSortState: SortState = { field: null, direction: 'asc' };

const TAB_DISPLAY_ORDER: Record<OrderedTab['key'], number> = {
    all: 0,
    confirmed: 1,
    ordered: 2,
    rejected: 3,
    cancelled: 4,
    returned: 4,
};

const OrderedTabs = ({
    tabs,
    refetch,
    vin,
    supplierNotes = [],
    supplierMessageCounts = {},
    onOpenSupplierCommunications,
    locationId = '',
    roNumber = '',
    vehicleMake = '',
    quotesOnly = false,
}: OrderedTabsProps) => {
    const session = useContext(SessionContext);
    const orgCanReceive = canMarkPartsReceived(session);
    const inventoryEnabled =
        session?.isAuthenticated === true && session.inventoryEnabled === true;
    const inventoryAllowed =
        session?.isAuthenticated === true && session.permissions.includes('inventory');
    const canSelectForInventory =
        inventoryEnabled && inventoryAllowed && canMovePartsToInventory(quotesOnly);
    const receiveMutation = useReceiveParts();
    const confirmSupplierOrderMutation = useConfirmSupplierOrder();
    const toast = useToast();
    const [sortStates, setSortStates] = useState<SectionSortState>({});
    const [selectedParts, setSelectedParts] = useState<Set<string>>(new Set());
    const [markConfirmTarget, setMarkConfirmTarget] = useState<{
        supplierOrderId: string;
        title: string;
    } | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedVendors, setSelectedVendors] = useState<Array<string> | 'all'>('all');
    const [bulkCancelModal, setBulkCancelModal] = useState<{
        supplierOrderId: string;
        source?: OrderedSectionInterface['source'];
        partIds: string[];
        parts: CancelReturnPartSummary[];
        action: 'return' | 'cancellation';
        cancelOnly: boolean;
    } | null>(null);
    const [inventoryModal, setInventoryModal] = useState<{
        supplierOrderId: string;
        parts: MoveToInventoryPartSnapshot[];
    } | null>(null);

    const displayTabs = useMemo(() => {
        const cancelledTab = tabs.find((tab) => tab.key === 'cancelled');
        const returnedTab = tabs.find((tab) => tab.key === 'returned');
        const result = tabs.filter((tab) => tab.key !== 'cancelled' && tab.key !== 'returned');

        if (cancelledTab || returnedTab) {
            const sections = [...(cancelledTab?.sections ?? [])];
            for (const section of returnedTab?.sections ?? []) {
                if (!sections.some((s) => s.key === section.key)) {
                    sections.push(section);
                }
            }
            result.push({
                key: 'cancelled',
                title: 'Cancelled / Returned',
                total: (cancelledTab?.total ?? 0) + (returnedTab?.total ?? 0),
                sections,
            });
        }

        return result.sort((a, b) => TAB_DISPLAY_ORDER[a.key] - TAB_DISPLAY_ORDER[b.key]);
    }, [tabs]);

    const vendorOptions = useMemo(() => {
        return Array.from(
            new Set(
                tabs
                    .flatMap((tab) => tab.sections)
                    .map((section) => section.title.trim())
                    .filter(Boolean)
            )
        ).sort((a, b) => a.localeCompare(b));
    }, [tabs]);

    const normalizedSelectedVendors = useMemo(() => {
        if (selectedVendors === 'all') return 'all';
        return selectedVendors.filter((vendor) => vendorOptions.includes(vendor));
    }, [selectedVendors, vendorOptions]);

    /** Flat map of every part → its supplier section (across display tabs). */
    const partSectionIndex = useMemo(() => {
        const index = new Map<string, OrderedSectionInterface>();
        tabs.forEach((tab) => {
            tab.sections.forEach((section) => {
                section.parts.forEach((part) => {
                    if (!index.has(part.partId)) index.set(part.partId, section);
                });
            });
        });
        return index;
    }, [tabs]);

    const selectedPartEntries = useMemo(() => (
        Array.from(selectedParts).flatMap((partId) => {
            const section = partSectionIndex.get(partId);
            if (!section) return [];
            const part = section.parts.find((p) => p.partId === partId);
            if (!part) return [];
            return [{ partId, part, section }];
        })
    ), [selectedParts, partSectionIndex]);

    const selectedReceivableIds = selectedPartEntries
        .filter(({ part, section }) => isPartReceivable(part, section.status))
        .map(({ partId }) => partId);

    const selectedUnreceivableIds = selectedPartEntries
        .filter(({ part }) => isPartUnreceivable(part))
        .map(({ partId }) => partId);

    const selectedSupplierOrderIds = useMemo(
        () => Array.from(new Set(selectedPartEntries.map(({ section }) => section.key))),
        [selectedPartEntries],
    );
    const singleSupplierSelected = selectedSupplierOrderIds.length === 1;

    // canManualConfirm is server-computed; place-order mirrors the API's gate.
    const canMarkConfirmed = session?.isAuthenticated === true && session.permissions.includes('place-order');
    const selectedConfirmSection = singleSupplierSelected && selectedPartEntries.length > 0
        ? selectedPartEntries[0].section
        : null;
    const canMarkConfirmedSelection =
        !!selectedConfirmSection
        && !!selectedConfirmSection.canManualConfirm
        && canMarkConfirmed;

    const allSelectedReturnable =
        selectedPartEntries.length > 0
        && selectedPartEntries.every(({ part, section }) =>
            isPartReturnable(part, section.status),
        );
    const allSelectedReceivable =
        selectedPartEntries.length > 0
        && selectedPartEntries.every(({ part, section }) =>
            isPartReceivable(part, section.status),
        );
    const allSelectedUnreceivable =
        selectedPartEntries.length > 0
        && selectedPartEntries.every(({ part }) => isPartUnreceivable(part));
    const allSelectedInventoryMovable =
        selectedPartEntries.length > 0
        && selectedPartEntries.every(({ part, section }) =>
            isPartInventoryMovable(part, section.status),
        );

    const selectedSection = singleSupplierSelected ? selectedPartEntries[0]?.section : undefined;
    const returnBlocked = !!selectedSection && isUnconfirmedInPartstrader(selectedSection);

    const bulkBarActions = useMemo((): BulkActionBarItem[] => {
        const actions: BulkActionBarItem[] = [];
        if (allSelectedReturnable && singleSupplierSelected) {
            actions.push({
                id: 'cancel',
                label: 'Cancel',
                icon: TbX as React.ElementType,
                danger: true,
            });
            if (!returnBlocked) {
                actions.push({
                    id: 'return',
                    label: 'Return',
                    icon: TbArrowBackUp as React.ElementType,
                });
            }
        }
        if (orgCanReceive && allSelectedReceivable) {
            actions.push({
                id: 'receive',
                label: 'Mark Received',
                icon: TbPackage as React.ElementType,
                success: true,
            });
        }
        if (orgCanReceive && allSelectedUnreceivable) {
            actions.push({
                id: 'unreceive',
                label: 'Undo received',
                icon: TbArrowBackUp as React.ElementType,
            });
        }
        if (canSelectForInventory && allSelectedInventoryMovable && singleSupplierSelected) {
            actions.push({
                id: 'inventory',
                label: 'Inventory',
                icon: TbBuildingWarehouse as React.ElementType,
            });
        }
        if (canMarkConfirmedSelection) {
            actions.push({
                id: 'confirm',
                label: 'Mark Confirmed',
                iconName: 'checkCircle',
                success: true,
            });
        }
        return actions;
    }, [
        allSelectedReturnable,
        allSelectedReceivable,
        allSelectedUnreceivable,
        allSelectedInventoryMovable,
        singleSupplierSelected,
        returnBlocked,
        orgCanReceive,
        canSelectForInventory,
        canMarkConfirmedSelection,
    ]);

    if (tabs.length === 0) {
        return null;
    }

    const getSortState = (sectionKey: string): SortState => sortStates[sectionKey] ?? defaultSortState;

    const toggleSort = (sectionKey: string, field: SortField) => {
        setSortStates((prev) => {
            const current = prev[sectionKey] ?? defaultSortState;

            if (current.field !== field) {
                return { ...prev, [sectionKey]: { field, direction: 'asc' } };
            }

            if (current.direction === 'asc') {
                return { ...prev, [sectionKey]: { field, direction: 'desc' } };
            }

            return { ...prev, [sectionKey]: defaultSortState };
        });
    };

    const togglePartSelection = (partId: string) => {
        setSelectedParts((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(partId)) {
                newSet.delete(partId);
            } else {
                newSet.add(partId);
            }
            return newSet;
        });
    };

    const toggleAllPartsInSection = (_sectionKey: string, partIds: string[]) => {
        if (partIds.length === 0) return;

        const allSelected = partIds.every((id) => selectedParts.has(id));

        setSelectedParts((prev) => {
            const newSet = new Set(prev);
            if (allSelected) {
                partIds.forEach((id) => newSet.delete(id));
            } else {
                partIds.forEach((id) => newSet.add(id));
            }
            return newSet;
        });
    };

    const clearSelection = () => setSelectedParts(new Set());

    const openBulkCancelReturn = (action: 'return' | 'cancellation') => {
        if (!allSelectedReturnable || !singleSupplierSelected) return;
        const section = selectedPartEntries[0].section;
        const unconfirmedPartstrader = isUnconfirmedInPartstrader(section);
        const livePartIds = section.parts.filter((p) => !p.cancelled && !p.returned).map((p) => p.partId);
        if (unconfirmedPartstrader && selectedPartEntries.length < livePartIds.length) {
            setSelectedParts((prev) => new Set(Array.from(prev).concat(livePartIds)));
            toast({
                title: `${section.title} hasn't confirmed this order in PartsTrader yet`,
                description: `PartsTrader can only cancel the whole order until they do, so all ${livePartIds.length} parts are now selected.`,
                status: 'warning',
                duration: 6000,
                isClosable: true,
            });
            return;
        }
        setBulkCancelModal({
            supplierOrderId: section.key,
            source: section.source,
            partIds: selectedPartEntries.map(({ partId }) => partId),
            parts: selectedPartEntries.map(({ part }) => ({
                partId: part.partId,
                name: part.name,
                partNumber: part.correctedPartNumber ?? part.partNumber,
            })),
            action,
            cancelOnly: unconfirmedPartstrader,
        });
    };

    const openBulkInventory = () => {
        if (!canSelectForInventory || !allSelectedInventoryMovable || !singleSupplierSelected) {
            return;
        }
        const section = selectedPartEntries[0].section;
        setInventoryModal({
            supplierOrderId: section.key,
            parts: selectedPartEntries.map(({ part }) => ({
                partId: part.partId,
                name: part.name,
                partNumber: part.correctedPartNumber ?? part.partNumber,
                quantity: getOrderedPartRemainingQuantity(part),
                buyPrice: part.correctedBuyPrice ?? part.buyPrice,
                vendorLabel: part.vendor || section.title,
            })),
        });
    };

    const handleMarkConfirmed = () => {
        if (!markConfirmTarget) return;
        confirmSupplierOrderMutation.mutate(
            { supplierOrderId: markConfirmTarget.supplierOrderId },
            {
                onSuccess: () => {
                    toast({
                        title: 'Order marked Confirmed',
                        description: `${markConfirmTarget.title} — parts moved to Confirmed.`,
                        status: 'success',
                    });
                    setMarkConfirmTarget(null);
                    clearSelection();
                    refetch();
                },
                onError: () => {
                    toast({
                        title: 'Could not mark order Confirmed',
                        description: 'Something went wrong. Please try again.',
                        status: 'error',
                    });
                },
            },
        );
    };

    const handleBulkAction = (actionId: string) => {
        if (actionId === 'confirm') {
            if (!canMarkConfirmedSelection || !selectedConfirmSection) return;
            setMarkConfirmTarget({
                supplierOrderId: selectedConfirmSection.key,
                title: selectedConfirmSection.title,
            });
            return;
        }
        if (actionId === 'cancel') {
            openBulkCancelReturn('cancellation');
            return;
        }
        if (actionId === 'return') {
            openBulkCancelReturn('return');
            return;
        }
        if (actionId === 'receive') {
            handleReceiveParts(selectedReceivableIds);
            return;
        }
        if (actionId === 'unreceive') {
            handleUndoReceiveParts(selectedUnreceivableIds);
            return;
        }
        if (actionId === 'inventory') {
            openBulkInventory();
        }
    };

    const handleUndoReceiveParts = (partIds: string[]) => {
        if (partIds.length === 0) return;
        receiveMutation.mutate(
            { orderPartIds: partIds, received: false },
            {
                onSuccess: (data) => {
                    clearSelection();
                    refetch();
                    const updated = data.updatedCount;
                    if (updated === 0) {
                        toast({
                            title: 'No receives were undone',
                            status: 'warning',
                            duration: 4000,
                            isClosable: true,
                        });
                        return;
                    }
                    toast({
                        title: updated === 1
                            ? 'Receive undone'
                            : `${updated} receives undone`,
                        description: updated < partIds.length
                            ? `${partIds.length - updated} part${partIds.length - updated === 1 ? ' was' : 's were'} skipped.`
                            : undefined,
                        status: updated < partIds.length ? 'warning' : 'success',
                        duration: updated < partIds.length ? 5000 : 2500,
                        isClosable: true,
                    });
                },
                onError: () => {
                    toast({
                        title: 'Failed to undo receive',
                        status: 'error',
                        duration: 4000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    const handleUndoReceive = (part: OrderedPart) => {
        receiveMutation.mutate(
            { orderPartIds: [part.partId], received: false },
            {
                onSuccess: () => {
                    refetch();
                    toast({
                        title: 'Receive undone',
                        description: `${part.name} is no longer marked received.`,
                        status: 'success',
                        duration: 2500,
                        isClosable: true,
                    });
                },
                onError: () => {
                    toast({
                        title: 'Failed to undo receive',
                        description: `${part.name} is still marked received.`,
                        status: 'error',
                        duration: 4000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    const handleReceiveParts = (partIds: string[]) => {
        if (partIds.length === 0) return;
        receiveMutation.mutate(
            { orderPartIds: partIds, received: true },
            {
                onSuccess: (data) => {
                    clearSelection();
                    refetch();
                    // Report what the server actually recorded — it silently
                    // skips orders that predate the receiving opt-in date, so
                    // updatedCount can be 0 (or less than requested).
                    const updated = data.updatedCount;
                    if (updated === 0) {
                        toast({
                            title: 'No parts were marked received',
                            description: 'This order predates when receiving was enabled for your organization, so its parts can\'t be marked received.',
                            status: 'warning',
                            duration: 5000,
                            isClosable: true,
                        });
                        return;
                    }
                    toast({
                        title: `${updated} part${updated === 1 ? '' : 's'} marked received`,
                        description: updated < partIds.length
                            ? `${partIds.length - updated} part${partIds.length - updated === 1 ? ' was' : 's were'} skipped (not eligible for receiving).`
                            : undefined,
                        status: updated < partIds.length ? 'warning' : 'success',
                        duration: updated < partIds.length ? 5000 : 2500,
                        isClosable: true,
                    });
                },
                onError: () => {
                    toast({
                        title: 'Failed to mark parts received',
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                    });
                },
            },
        );
    };

    return (
        <Box>
            <Tabs variant="unstyled" colorScheme="blue">
                <Flex justify="space-between" align="flex-end" mb={4} borderBottom="1.5px solid" borderColor="gray.200" mx={-6} px={6}>
                    <TabList border="none" gap={0}>
                        {displayTabs.map((tab) => (
                            <Tab
                                key={tab.key}
                                fontWeight="medium"
                                fontSize="sm"
                                color="gray.500"
                                bg="transparent"
                                border="none"
                                borderBottom="1.5px solid"
                                borderColor="transparent"
                                mb="-1.5px"
                                py={2}
                                px={3}
                                _selected={{
                                    color: "gray.800",
                                    borderColor: "gray.500",
                                }}
                                _hover={{
                                    color: "gray.700",
                                }}
                            >
                                <HStack spacing={0}>
                                    <Text>{tab.title}</Text>
                                    {tab.total > 0 && (
                                        <Flex
                                            ml={1.5}
                                            bg="gray.100"
                                            borderRadius="lg"
                                            h="18px"
                                            minW="18px"
                                            px="5px"
                                            align="center"
                                            justify="center"
                                            flexShrink={0}
                                        >
                                            <Text
                                                fontSize="11px"
                                                fontWeight="bold"
                                                lineHeight="1"
                                                color="gray.800"
                                            >
                                                {tab.total}
                                            </Text>
                                        </Flex>
                                    )}
                                </HStack>
                            </Tab>
                        ))}
                    </TabList>
                    <HStack spacing={2} pb={2} align="center">
                        <CollapsibleSearch
                            isCompact={false}
                            width="350px"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <VendorSelector
                            vendors={vendorOptions}
                            selectedVendors={normalizedSelectedVendors}
                            onVendorChange={setSelectedVendors}
                            width="240px"
                        />
                    </HStack>
                </Flex>

                <TabPanels>
                    {displayTabs.map((tab) => {
                        const vendorFilter =
                            normalizedSelectedVendors === 'all'
                                ? null
                                : new Set(normalizedSelectedVendors);
                        const visibleSections = tab.sections.filter((section) => {
                            if (vendorFilter && !vendorFilter.has(section.title.trim())) return false;
                            if (!searchQuery.trim()) return true;
                            const q = searchQuery.toLowerCase().trim();
                            return section.parts.some((part) =>
                                part.name.toLowerCase().includes(q) ||
                                (part.partNumber && part.partNumber.toLowerCase().includes(q)) ||
                                (part.correctedPartNumber && part.correctedPartNumber.toLowerCase().includes(q)) ||
                                (part.supplierPartNumber && part.supplierPartNumber.toLowerCase().includes(q)) ||
                                (part.type && part.type.toLowerCase().includes(q)) ||
                                (part.delivery && part.delivery.toLowerCase().includes(q))
                            );
                        });

                        return (
                            <TabPanel key={tab.key} px={0} py={0}>
                                <VStack spacing={6} align="stretch">
                                    {visibleSections.map((section) => (
										<Box key={section.key} bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="none" overflow="hidden">
                                            <OrderedSection
                                                section={section}
                                                showStatusTag={tab.key === 'all'}
                                                sortState={getSortState(section.key)}
                                                onToggleSort={(field) => toggleSort(section.key, field)}
                                                selectedParts={selectedParts}
                                                onTogglePartSelection={togglePartSelection}
                                                onToggleAllParts={toggleAllPartsInSection}
                                                refetch={refetch}
                                                vin={vin}
                                                searchQuery={searchQuery}
                                                supplierNotes={supplierNotes}
                                                supplierMessageCounts={supplierMessageCounts}
                                                onOpenSupplierCommunications={onOpenSupplierCommunications}
                                                orgCanReceive={orgCanReceive}
                                                canSelectForInventory={canSelectForInventory}
                                                canMarkConfirmed={canMarkConfirmed}
                                                isReceivePending={receiveMutation.isPending}
                                                onUndoReceive={handleUndoReceive}
                                            />
                                        </Box>
                                    ))}
                                </VStack>
                            </TabPanel>
                        );
                    })}
                </TabPanels>
            </Tabs>

            <BulkActionBar
                selectedCount={selectedParts.size}
                actions={bulkBarActions}
                onClearSelection={clearSelection}
                onAction={handleBulkAction}
                isLoading={receiveMutation.isPending}
                noActionsMessage="No shared actions available. Try selecting parts from only one supplier."
            />

            <CancelReturnPartsModal
                isOpen={bulkCancelModal !== null}
                onClose={() => setBulkCancelModal(null)}
                supplierOrderId={bulkCancelModal?.supplierOrderId ?? ''}
                source={bulkCancelModal?.source}
                selectedPartIds={bulkCancelModal?.partIds ?? []}
                parts={bulkCancelModal?.parts ?? []}
                initialAction={bulkCancelModal?.action ?? 'cancellation'}
                cancelOnly={bulkCancelModal?.cancelOnly}
                onSuccess={() => {
                    clearSelection();
                    refetch();
                    setBulkCancelModal(null);
                }}
            />

            <MoveToInventoryConfirmModal
                isOpen={inventoryModal !== null}
                onClose={() => setInventoryModal(null)}
                onBackToActions={() => setInventoryModal(null)}
                onSuccess={() => {
                    clearSelection();
                    refetch();
                    setInventoryModal(null);
                }}
                supplierOrderId={inventoryModal?.supplierOrderId ?? ''}
                parts={inventoryModal?.parts ?? []}
                locationId={locationId}
                roNumber={roNumber}
                vehicleMake={vehicleMake}
            />

            <ConfirmDialog
                isOpen={markConfirmTarget !== null}
                onClose={() => setMarkConfirmTarget(null)}
                onConfirm={handleMarkConfirmed}
                title="Mark order confirmed?"
                confirmLabel="Mark Confirmed"
                confirmColorScheme="green"
                isConfirmLoading={confirmSupplierOrderMutation.isPending}
            >
                {markConfirmTarget?.title} never confirmed this order through the
                marketplace. This marks the order and its active parts as Confirmed — use it
                when the vendor confirmed another way (e.g. over the phone). Re-keying may
                start once every part on the RO is confirmed.
            </ConfirmDialog>
        </Box>
    );
};

export default OrderedTabs;
